import { api } from './client';

export const reportsApi = {
  // === 통계 및 인사이트 ===
  getStatistics: async (): Promise<any> => {
    const response = await api.get('/api/stats');
    return response.data;
  },

  getInsights: async (refresh: boolean = false): Promise<any> => {
    const response = await api.get('/api/insights', {
      params: refresh ? { refresh: true } : {},
      timeout: refresh ? 120000 : 30000,
    });
    return response.data;
  },

  getExecutiveStats: async (): Promise<any> => {
    const response = await api.get('/api/stats/executive');
    return response.data;
  },

  getRegionDistribution: async (): Promise<any> => {
    const response = await api.get('/api/stats/region');
    return response.data;
  },

  clearCache: async (): Promise<{ message: string }> => {
    const response = await api.post('/api/cache/clear');
    return response.data;
  },

  // === 코호트 / DEI / AI 분석 ===
  getCohortAnalysis: async (groupBy: string = 'year'): Promise<any> => {
    const response = await api.get('/api/cohort/analysis', { params: { group_by: groupBy } });
    return response.data;
  },

  getDEIAnalysis: async (): Promise<any> => {
    const response = await api.get('/api/dei/analysis');
    return response.data;
  },

  getCohortAIAnalysis: async (refresh: boolean = false): Promise<any> => {
    const response = await api.get('/api/cohort/ai-analysis', { params: { refresh } });
    return response.data;
  },

  getDEIAIAnalysis: async (refresh: boolean = false): Promise<any> => {
    const response = await api.get('/api/dei/ai-analysis', { params: { refresh } });
    return response.data;
  },

  getOrgEfficiencyAIAnalysis: async (refresh: boolean = false): Promise<any> => {
    const response = await api.get('/api/org-efficiency/ai-analysis', { params: { refresh } });
    return response.data;
  },

  getPersonnelTrends: async (months: number = 12): Promise<any> => {
    const response = await api.get('/api/cohort/personnel-trends', { params: { months } });
    return response.data;
  },

  getAllPersonnelChanges: async (limit: number = 100, changeType?: string, orgUnitId?: number): Promise<any> => {
    const params: Record<string, any> = { limit };
    if (changeType) params.change_type = changeType;
    if (orgUnitId) params.org_unit_id = orgUnitId;
    const response = await api.get('/api/personnel-changes', { params });
    return response.data;
  },

  deletePersonnelChange: async (changeId: number): Promise<{ message: string; deleted_id: number }> => {
    const response = await api.delete(`/api/personnel-changes/${changeId}`);
    return response.data;
  },

  // === 리포트 ===
  getReportTypes: async (): Promise<any[]> => {
    const response = await api.get('/api/reports');
    return response.data?.reports || [];
  },

  downloadReport: async (reportId: string, format: string = 'excel', filters?: Record<string, string>): Promise<Blob> => {
    const slug = reportId.replace(/_/g, '-');
    const params = new URLSearchParams({ format });
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    }
    const response = await api.get(`/api/reports/${slug}?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  previewReport: async (reportId: string, filters?: Record<string, string>, limit: number = 50): Promise<any> => {
    const slug = reportId.replace(/_/g, '-');
    const params = new URLSearchParams({ limit: String(limit) });
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    }
    const response = await api.get(`/api/reports/preview/${slug}?${params.toString()}`);
    return response.data;
  },

  getReportHistory: async (limit: number = 20, offset: number = 0): Promise<any> => {
    const response = await api.get(`/api/reports/history?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  // === 리포트 스케줄링 (BACK-171) ===
  getReportScheduleTypes: async (): Promise<any> => {
    const response = await api.get('/api/report-schedules/types');
    return response.data;
  },

  createReportSchedule: async (data: any): Promise<any> => {
    const response = await api.post('/api/report-schedules', data);
    return response.data;
  },

  getReportSchedules: async (): Promise<any> => {
    const response = await api.get('/api/report-schedules');
    return response.data;
  },

  getReportSchedule: async (id: number): Promise<any> => {
    const response = await api.get(`/api/report-schedules/${id}`);
    return response.data;
  },

  updateReportSchedule: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/api/report-schedules/${id}`, data);
    return response.data;
  },

  deleteReportSchedule: async (id: number): Promise<any> => {
    const response = await api.delete(`/api/report-schedules/${id}`);
    return response.data;
  },

  // === 법적 서면 통보 문서 ===
  getDocumentTypes: async (): Promise<any> => {
    const response = await api.get('/api/documents');
    return response.data;
  },

  downloadAnnualLeaveNotice: async (employeeId: string, year?: number): Promise<Blob> => {
    const params = year ? `?year=${year}` : '';
    const response = await api.get(`/api/documents/annual-leave-notice/${employeeId}${params}`, { responseType: 'blob' });
    return response.data;
  },

  downloadEvaluationNotice: async (employeeId: string, periodId?: number): Promise<Blob> => {
    const params = periodId ? `?period_id=${periodId}` : '';
    const response = await api.get(`/api/documents/evaluation-notice/${employeeId}${params}`, { responseType: 'blob' });
    return response.data;
  },

  downloadPersonnelOrder: async (employeeId: string, changeType: string, previousValue: string, newValue: string, effectiveDate?: string): Promise<Blob> => {
    const params = new URLSearchParams({ change_type: changeType, previous_value: previousValue, new_value: newValue });
    if (effectiveDate) params.append('effective_date', effectiveDate);
    const response = await api.get(`/api/documents/personnel-order/${employeeId}?${params.toString()}`, { responseType: 'blob' });
    return response.data;
  },

  // === MBO/KPI 목표 관리 ===
  getGoals: async (periodId?: number, employeeId?: string, goalType?: string, tier?: number): Promise<any> => {
    const params: any = {};
    if (periodId) params.period_id = periodId;
    if (employeeId) params.employee_id = employeeId;
    if (goalType) params.goal_type = goalType;
    if (tier) params.tier = tier;
    const response = await api.get('/api/goals/', { params });
    return response.data;
  },

  getGoal: async (goalId: number): Promise<any> => {
    const response = await api.get(`/api/goals/${goalId}`);
    return response.data;
  },

  createGoal: async (data: { period_id: number; employee_id: string; goal_type: string; title: string; weight: number; parent_goal_id?: number; org_unit_id?: number; description?: string; metric_name?: string; metric_unit?: string; target_value?: number; start_date?: string; due_date?: string; company_id?: number; tier?: number }): Promise<any> => {
    const response = await api.post('/api/goals/', data);
    return response.data;
  },

  updateGoal: async (goalId: number, data: { title?: string; description?: string; weight?: number; target_value?: number; status?: string; start_date?: string; due_date?: string }): Promise<any> => {
    const response = await api.put(`/api/goals/${goalId}`, data);
    return response.data;
  },

  deleteGoal: async (goalId: number): Promise<any> => {
    const response = await api.delete(`/api/goals/${goalId}`);
    return response.data;
  },

  approveGoal: async (goalId: number): Promise<any> => {
    const response = await api.post(`/api/goals/${goalId}/approve`);
    return response.data;
  },

  getTeamGoals: async (orgUnitId: number, periodId: number): Promise<any> => {
    const response = await api.get(`/api/goals/team/${orgUnitId}`, { params: { period_id: periodId } });
    return response.data;
  },

  getCascadingTree: async (goalId: number): Promise<any> => {
    const response = await api.get(`/api/goals/${goalId}/cascading`);
    return response.data;
  },

  addGoalProgress: async (goalId: number, data: { record_date: string; actual_value?: number; achievement_rate?: number; description?: string; evidence?: string }): Promise<any> => {
    const response = await api.post(`/api/goals/${goalId}/progress`, data);
    return response.data;
  },

  getGoalProgress: async (goalId: number): Promise<any> => {
    const response = await api.get(`/api/goals/${goalId}/progress`);
    return response.data;
  },

  createGoalReview: async (goalId: number, data: { review_date: string; content: string; review_type?: string; revised_target_value?: number; revised_due_date?: string; recommendation?: string }): Promise<any> => {
    const response = await api.post(`/api/goals/${goalId}/review`, data);
    return response.data;
  },

  getGoalReviews: async (goalId: number): Promise<any> => {
    const response = await api.get(`/api/goals/${goalId}/reviews`);
    return response.data;
  },

  getEmployeeAchievement: async (employeeId: string, periodId: number): Promise<any> => {
    const response = await api.get(`/api/goals/achievement/${employeeId}`, { params: { period_id: periodId } });
    return response.data;
  },

  getAchievementSummary: async (periodId: number): Promise<any> => {
    const response = await api.get('/api/goals/summary', { params: { period_id: periodId } });
    return response.data;
  },

  getStrategicKPIs: async (periodId: number, companyId?: number): Promise<any> => {
    const params: any = { period_id: periodId };
    if (companyId) params.company_id = companyId;
    const response = await api.get('/api/goals/strategic', { params });
    return response.data;
  },

  deployStrategicKPI: async (goalId: number, targets: Array<{ employee_id: string; weight?: number; target_value?: number; title?: string }>): Promise<any> => {
    const response = await api.post(`/api/goals/${goalId}/deploy`, { targets });
    return response.data;
  },

  rollupGoal: async (goalId: number): Promise<any> => {
    const response = await api.post(`/api/goals/${goalId}/rollup`);
    return response.data;
  },

  // MYB-453: Goal alignment tree (Tier-1 → Tier-2 cascade)
  getGoalAlignmentTree: async (periodId: number): Promise<any> => {
    const response = await api.get(`/api/goals/tree/${periodId}`);
    return response.data;
  },
};
